import React from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

const SuccessScreen = ({ request, actionType = "Approved", onBackToDashboard }) => {
  const isApproved = actionType === "Approved";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "480px",
        margin: "0 auto",
        minHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: 2.5, sm: 3 },
      }}
    >
      {/* Big Circular Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          backgroundColor: isApproved ? "#22C55E" : "#EF4444",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#ffffff",
          mb: 3,
          boxShadow: isApproved
            ? "0 10px 25px rgba(34, 197, 94, 0.35)"
            : "0 10px 25px rgba(239, 68, 68, 0.35)",
        }}
      >
        {isApproved ? (
          <CheckIcon sx={{ fontSize: 44, stroke: "#ffffff", strokeWidth: 1.5 }} />
        ) : (
          <CloseIcon sx={{ fontSize: 44, stroke: "#ffffff", strokeWidth: 1.5 }} />
        )}
      </Box>

      {/* Main Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: "#0F172A",
          fontSize: { xs: "1.45rem", sm: "1.6rem" },
          mb: 1.5,
          textAlign: "center",
        }}
      >
        {isApproved ? "Request Approved" : "Request Rejected"}
      </Typography>

      {/* Subtitle Message */}
      <Typography
        sx={{
          color: "#475569",
          fontSize: "0.95rem",
          textAlign: "center",
          lineHeight: 1.5,
          maxWidth: "340px",
          mb: 3.5,
        }}
      >
        The travel request for{" "}
        <Box component="span" sx={{ fontWeight: 700, color: "#0F172A" }}>
          {request?.name || "the employee"}
        </Box>{" "}
        has been {isApproved ? "approved successfully" : "rejected"}.
      </Typography>

      {/* Summary Card */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          backgroundColor: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "16px",
          p: 2.5,
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Employee No */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
            <BadgeOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: "0.9rem", color: "#64748B", fontWeight: 500 }}>
              Employee No
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#1E293B" }}>
            {request?.employeeNo || "0003596"}
          </Typography>
        </Box>

        {/* Name */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
            <PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: "0.9rem", color: "#64748B", fontWeight: 500 }}>
              Name
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#1E293B" }}>
            {request?.name || "Amila Bandara"}
          </Typography>
        </Box>

        {/* Destination */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
            <FlightTakeoffIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: "0.9rem", color: "#64748B", fontWeight: 500 }}>
              Destination
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#1E293B" }}>
            {request?.country || "Canada"}
          </Typography>
        </Box>

        {/* Travel Date */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: "0.9rem", color: "#64748B", fontWeight: 500 }}>
              Travel Date
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#1E293B" }}>
            {request?.dateRange || "02 Oct 2026 - 16 Oct 2026"}
          </Typography>
        </Box>
      </Paper>

      {/* Back to Dashboard Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={onBackToDashboard}
        sx={{
          backgroundColor: "#0052CC",
          color: "#ffffff",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "1rem",
          borderRadius: "14px",
          py: 1.4,
          boxShadow: "0 4px 14px rgba(0, 82, 204, 0.28)",
          "&:hover": {
            backgroundColor: "#0040A8",
          },
        }}
      >
        Back to Dashboard
      </Button>
    </Box>
  );
};

export default SuccessScreen;
